// Simple API client for the frontend
const API_BASE = process.env.REACT_APP_API_BASE || 'https://qorenova-backend-production.up.railway.app';

async function apiFetch(path, { method = 'GET', body, token } = {}) {
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
    credentials: 'include',
  });

  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    if (res.status === 401) {
      const msg = data?.message || 'Unauthorized';
      throw new Error(msg);
    }
    const msg = data?.message || `Request failed: ${res.status}`;
    throw new Error(msg);
  }
  return data;
}

function toQuery(params = {}) {
  const entries = Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '');
  if (!entries.length) return '';
  return '?' + entries.map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`).join('&');
}

export const AuthApi = {
  me: (token) => apiFetch('/api/auth/me', { token }),
  updateMe: (token, data) => apiFetch('/api/auth/me', { method: 'PUT', token, body: data }),
  changePassword: (token, data) => apiFetch('/api/auth/change-password', { method: 'PUT', token, body: data }),
};

export const EnrollmentApi = {
  listMine: (token, params) => apiFetch('/api/enrollments' + toQuery(params), { token }),
  get: (token, id) => apiFetch(`/api/enrollments/${id}`, { token }),
  analytics: (token, id) => apiFetch(`/api/enrollments/${id}/analytics`, { token }),
  progress: (token, id) => apiFetch(`/api/enrollments/${id}/progress`, { token }),
  validateCode: (token, code) => apiFetch('/api/enrollments/validate-code', { 
    method: 'POST', 
    token, 
    body: { code } 
  }),
  checkLabAccess: (token, labId) => apiFetch(`/api/enrollments/lab-access/${labId}`, { token }),
};

export const CourseApi = {
  list: (params = {}) => apiFetch('/api/courses', { params }),
  getById: (id) => apiFetch(`/api/courses/${id}`),
  enroll: (token, id) => apiFetch(`/api/courses/${id}/enroll`, { method: 'POST', token }),
};

export const PaymentApi = {
  createIntent: (token, data) => apiFetch('/api/payments/create-intent', { 
    method: 'POST', 
    token, 
    body: JSON.stringify(data) 
  }),
  confirm: (token, data) => apiFetch('/api/payments/confirm', { 
    method: 'POST', 
    token, 
    body: JSON.stringify(data) 
  }),
  getPayments: (token, params = {}) => apiFetch('/api/payments', { token, params }),
  getPayment: (token, id) => apiFetch(`/api/payments/${id}`, { token }),
};

export const VirtualLabApi = {
  // Public endpoints
  getAllLabs: (params = {}) => apiFetch('/api/virtual-labs' + toQuery(params)),
  getLabById: (labId) => apiFetch(`/api/virtual-labs/${labId}`),
  
  // Protected endpoints - require token
  getLabsWithAccess: (token) => apiFetch('/api/virtual-labs/user/with-access', { token }),
  getMyKeys: (token) => apiFetch('/api/virtual-labs/user/my-keys', { token }),
  checkLabAccess: (token, labId) => apiFetch(`/api/virtual-labs/${labId}/check-access`, { token }),
  generateKey: (token, labId, enrollmentId) => apiFetch(`/api/virtual-labs/${labId}/generate-key`, { 
    method: 'POST', 
    token, 
    body: { enrollmentId } 
  }),
  validateKey: (token, key, labId) => apiFetch('/api/virtual-labs/validate-key', { 
    method: 'POST', 
    token, 
    body: { key, labId } 
  }),
  
  // Admin endpoints
  createLab: (token, labData) => apiFetch('/api/virtual-labs', { 
    method: 'POST', 
    token, 
    body: labData 
  }),
  updateLab: (token, labId, labData) => apiFetch(`/api/virtual-labs/${labId}`, { 
    method: 'PUT', 
    token, 
    body: labData 
  }),
  seedLabs: (token) => apiFetch('/api/virtual-labs/admin/seed', { method: 'POST', token }),
};

// Coaching/Mentorship API
export const CoachingApi = {
  // Mentor endpoints
  getMentors: (params = {}) => apiFetch('/api/coaching/mentors' + toQuery(params)),
  getMentorById: (id) => apiFetch(`/api/coaching/mentors/${id}`),
  getMyMentor: (token) => apiFetch('/api/coaching/mentors/me', { token }),
  getMentorStudio: (token) => apiFetch('/api/coaching/mentors/me/studio', { token }),
  createMentor: (token, data) => apiFetch('/api/coaching/mentors', { method: 'POST', token, body: data }),
  updateMentor: (token, id, data) => apiFetch(`/api/coaching/mentors/${id}`, { method: 'PUT', token, body: data }),
  
  // Session endpoints
  getSessions: (token, params = {}) => apiFetch('/api/coaching/sessions/mine' + toQuery(params), { token }),
  getMySessions: (token, params = {}) => apiFetch('/api/coaching/sessions/mine' + toQuery(params), { token }),
  createSession: (token, data) => apiFetch('/api/coaching/sessions', { method: 'POST', token, body: data }),
  requestSession: (token, data) => apiFetch('/api/coaching/sessions', { method: 'POST', token, body: data }),
  respondSession: (token, id, action, extra = {}) => apiFetch(`/api/coaching/sessions/${id}/respond`, { method: 'PUT', token, body: { action, ...extra } }),
  startSession: (token, id) => apiFetch(`/api/coaching/sessions/${id}/start`, { method: 'POST', token }),
  updateSession: (token, id, data) => apiFetch(`/api/coaching/sessions/${id}`, { method: 'PUT', token, body: data }),
  
  // Program endpoints
  getPrograms: (params = {}) => apiFetch('/api/coaching/programs' + toQuery(params)),
  getProgramById: (id) => apiFetch(`/api/coaching/programs/${id}`),
  
  // Mentorship endpoints
  createMentorship: (token, data) => apiFetch('/api/coaching/mentorships', { method: 'POST', token, body: data }),
  getMentorships: (token, params = {}) => apiFetch('/api/coaching/mentorships' + toQuery(params), { token }),
};

// AI API for mentorship
export const AIApi = {
  chat: (token, data) => apiFetch('/api/ai/chat', { method: 'POST', token, body: data }),
  parseGoal: (token, goal) => apiFetch('/api/ai/parse-goal', { method: 'POST', token, body: { goal } }),
};

// Career/Job API
export const CareerApi = {
  // Job search & matching
  searchJobs: (params = {}) => apiFetch('/api/career/jobs/search' + toQuery(params)),
  getMatchingJobs: (token, params = {}) => apiFetch('/api/career/jobs/matching' + toQuery(params), { token }),
  getRecentJobs: (params = {}) => apiFetch('/api/career/jobs/recent' + toQuery(params)),
  
  // Real-time job fetching from external APIs
  getRealTimeJobs: (params = {}) => apiFetch('/api/career/jobs/realtime' + toQuery(params)),
  getAvailableSources: () => apiFetch('/api/career/sources'),
  
  // Market stats
  getMarketStats: () => apiFetch('/api/career/stats/overview'),
  
  // Skill insights
  getSkillDemand: (skill, region = 'global') => apiFetch(`/api/career/skill-demand/${skill}` + toQuery({ region })),
  getEmergingSkills: (industry, timeframe = '12months') => apiFetch(`/api/career/emerging-skills/${industry}` + toQuery({ timeframe })),
  getSalaryForecast: (role, params = {}) => apiFetch(`/api/career/salary-forecast/${role}` + toQuery(params)),
  
  // AI Application Guidance
  getApplicationGuidance: (token, data) => apiFetch('/api/career/ai/application-guidance', { method: 'POST', token, body: data }),
  analyzeResume: (token, data) => apiFetch('/api/career/ai/analyze-resume', { method: 'POST', token, body: data }),
  generateCoverLetter: (token, data) => apiFetch('/api/career/ai/generate-cover-letter', { method: 'POST', token, body: data }),
  
  // Saved jobs
  saveJob: (token, jobId) => apiFetch(`/api/career/jobs/${jobId}/save`, { method: 'POST', token }),
  unsaveJob: (token, jobId) => apiFetch(`/api/career/jobs/${jobId}/unsave`, { method: 'DELETE', token }),
  getSavedJobs: (token) => apiFetch('/api/career/jobs/saved', { token }),
  
  // Applications
  applyToJob: (token, jobId, data) => apiFetch(`/api/career/jobs/${jobId}/apply`, { method: 'POST', token, body: data }),
  getMyApplications: (token) => apiFetch('/api/career/applications', { token }),
};

// Community API
export const CommunityApi = {
  // Stats & Overview
  getStats: () => apiFetch('/api/community/stats'),
  getOverview: () => apiFetch('/api/community/overview'),
  
  // Discussions
  getDiscussions: (params = {}) => apiFetch('/api/community/discussions' + toQuery(params)),
  getDiscussion: (id) => apiFetch(`/api/community/discussions/${id}`),
  getTrendingTopics: (limit = 5) => apiFetch(`/api/community/discussions/trending?limit=${limit}`),
  createDiscussion: (token, data) => apiFetch('/api/community/discussions', { method: 'POST', token, body: data }),
  replyToDiscussion: (token, id, data) => apiFetch(`/api/community/discussions/${id}/reply`, { method: 'POST', token, body: data }),
  likeDiscussion: (token, id) => apiFetch(`/api/community/discussions/${id}/like`, { method: 'POST', token }),
  
  // Categories
  getCategories: () => apiFetch('/api/community/categories'),
  
  // Events
  getEvents: (params = {}) => apiFetch('/api/community/events' + toQuery(params)),
  getEvent: (id) => apiFetch(`/api/community/events/${id}`),
  registerForEvent: (token, id) => apiFetch(`/api/community/events/${id}/register`, { method: 'POST', token }),
  
  // Members
  getMembers: (params = {}) => apiFetch('/api/community/members' + toQuery(params)),
  getFeaturedMembers: () => apiFetch('/api/community/members/featured'),
  getMemberProfile: (id) => apiFetch(`/api/community/members/${id}`),
  
  // Activity Feed
  getActivityFeed: (params = {}) => apiFetch('/api/community/activity' + toQuery(params)),
  
  // Announcements
  getAnnouncements: () => apiFetch('/api/community/announcements'),
};

// Admin API
export const AdminApi = {
  // Dashboard
  getDashboard: (token) => apiFetch('/api/admin/dashboard', { token }),
  getAnalytics: (token, params = {}) => apiFetch('/api/admin/analytics' + toQuery(params), { token }),
  
  // Users
  getUsers: (token, params = {}) => apiFetch('/api/admin/users' + toQuery(params), { token }),
  createUser: (token, data) => apiFetch('/api/admin/users', { method: 'POST', token, body: data }),
  updateUser: (token, id, data) => apiFetch(`/api/admin/users/${id}`, { method: 'PUT', token, body: data }),
  deleteUser: (token, id) => apiFetch(`/api/admin/users/${id}`, { method: 'DELETE', token }),
  
  // Courses
  getCourses: (token, params = {}) => apiFetch('/api/admin/courses' + toQuery(params), { token }),
  createCourse: (token, data) => apiFetch('/api/admin/courses', { method: 'POST', token, body: data }),
  updateCourse: (token, id, data) => apiFetch(`/api/admin/courses/${id}`, { method: 'PUT', token, body: data }),
  deleteCourse: (token, id, force = false) => apiFetch(`/api/admin/courses/${id}${force ? '?force=true' : ''}`, { method: 'DELETE', token }),
  getPendingApprovals: (token) => apiFetch('/api/admin/courses/pending-approval', { token }),
  reviewCourse: (token, id, data) => apiFetch(`/api/admin/courses/${id}/review`, { method: 'POST', token, body: data }),
  
  // Instructors
  getInstructors: (token, params = {}) => apiFetch('/api/admin/instructors' + toQuery(params), { token }),
  approveInstructor: (token, id) => apiFetch(`/api/admin/instructors/${id}/approve`, { method: 'POST', token }),
  rejectInstructor: (token, id, reason) => apiFetch(`/api/admin/instructors/${id}/reject`, { method: 'POST', token, body: { reason } }),
  
  // Enrollments
  getEnrollments: (token, params = {}) => apiFetch('/api/admin/enrollments' + toQuery(params), { token }),
  updateEnrollment: (token, id, data) => apiFetch(`/api/admin/enrollments/${id}`, { method: 'PUT', token, body: data }),
  
  // Activity Logs
  getActivityLogs: (token, params = {}) => apiFetch('/api/admin/activity-logs' + toQuery(params), { token }),
  getActivityStats: (token, params = {}) => apiFetch('/api/admin/activity-logs/stats' + toQuery(params), { token }),
  
  // System Health
  getSystemHealth: (token) => apiFetch('/api/admin/system-health', { token }),
  
  // Settings
  getSettings: (token) => apiFetch('/api/admin/settings', { token }),
  updateSettings: (token, data) => apiFetch('/api/admin/settings', { method: 'PUT', token, body: data }),
  
  // AI Admin Agent - NOVA v3.0
  getAgentStatus: (token) => apiFetch('/api/admin/ai-agent/status', { token }),
  getAgentHealth: (token) => apiFetch('/api/admin/ai-agent/health', { token }),
  runAgentScan: (token) => apiFetch('/api/admin/ai-agent/scan', { method: 'POST', token }),
  getAgentInsights: (token, timeframe = '7d') => apiFetch(`/api/admin/ai-agent/insights?timeframe=${timeframe}`, { token }),
  chatWithAgent: (token, message) => apiFetch('/api/admin/ai-agent/chat', { method: 'POST', token, body: { message } }),
  executeAgentAction: (token, actionType, params) => apiFetch('/api/admin/ai-agent/action', { method: 'POST', token, body: { actionType, params } }),
  
  // Sub-Agents & CEO Communication
  getSubAgents: (token) => apiFetch('/api/admin/ai-agent/sub-agents', { token }),
  getCEOAlerts: (token) => apiFetch('/api/admin/ai-agent/ceo-alerts', { token }),
  acknowledgeCEOAlert: (token, alertId) => apiFetch(`/api/admin/ai-agent/ceo-alerts/${alertId}/acknowledge`, { method: 'POST', token }),
  delegateToSubAgent: (token, agentName, task, params) => apiFetch('/api/admin/ai-agent/delegate', { method: 'POST', token, body: { agentName, task, params } }),
  getAgentActivity: (token, limit = 50) => apiFetch(`/api/admin/ai-agent/activity?limit=${limit}`, { token }),
  setAutonomousMode: (token, enabled) => apiFetch('/api/admin/ai-agent/autonomous', { method: 'POST', token, body: { enabled } }),
  
  // Mentorship Management
  getMentorshipStats: (token) => apiFetch('/api/admin/mentorship/stats', { token }),
  getMentorsAdmin: (token, params = {}) => apiFetch('/api/admin/mentorship/mentors' + toQuery(params), { token }),
  approveMentor: (token, id) => apiFetch(`/api/admin/mentorship/mentors/${id}/approve`, { method: 'PUT', token }),
  rejectMentor: (token, id, reason) => apiFetch(`/api/admin/mentorship/mentors/${id}/reject`, { method: 'PUT', token, body: { reason } }),
  updateMentorStatus: (token, id, status, reason) => apiFetch(`/api/admin/mentorship/mentors/${id}/status`, { method: 'PUT', token, body: { status, reason } }),
  getMentorshipRequests: (token, params = {}) => apiFetch('/api/admin/mentorship/requests' + toQuery(params), { token }),
  getMentoringSessions: (token, params = {}) => apiFetch('/api/admin/mentorship/sessions' + toQuery(params), { token }),
  updateMentorshipRequestStatus: (token, id, status, reason) => apiFetch(`/api/admin/mentorship/requests/${id}/status`, { method: 'PUT', token, body: { status, reason } }),
  updateSessionStatus: (token, id, status) => apiFetch(`/api/admin/mentorship/sessions/${id}/status`, { method: 'PUT', token, body: { status } }),
  
  // Contact Messages Management
  getContactMessages: (token, params = {}) => apiFetch('/api/contact/admin/messages' + toQuery(params), { token }),
  getContactStats: (token) => apiFetch('/api/contact/admin/stats', { token }),
  getContactMessage: (token, id) => apiFetch(`/api/contact/admin/messages/${id}`, { token }),
  updateContactMessage: (token, id, data) => apiFetch(`/api/contact/admin/messages/${id}`, { method: 'PUT', token, body: data }),
  deleteContactMessage: (token, id) => apiFetch(`/api/contact/admin/messages/${id}`, { method: 'DELETE', token }),
  archiveContactMessage: (token, id) => apiFetch(`/api/contact/admin/messages/${id}/archive`, { method: 'POST', token }),
  markContactAsSpam: (token, id) => apiFetch(`/api/contact/admin/messages/${id}/spam`, { method: 'POST', token }),
  
  // E-Learning / AI Course Generation Management
  getElearningStats: (token) => apiFetch('/api/admin/elearning/stats', { token }),
  getGeneratedPaths: (token, params = {}) => apiFetch('/api/admin/elearning/paths' + toQuery(params), { token }),
  getPathDetails: (token, pathId) => apiFetch(`/api/admin/elearning/paths/${pathId}`, { token }),
  deleteLearningPath: (token, pathId) => apiFetch(`/api/admin/elearning/paths/${pathId}`, { method: 'DELETE', token }),
  getUserGenerationStats: (token, params = {}) => apiFetch('/api/admin/elearning/user-stats' + toQuery(params), { token }),
  updateUserGenerationLimits: (token, userId, limits) => apiFetch(`/api/admin/elearning/users/${userId}/limits`, { method: 'PUT', token, body: limits }),
  getGlobalGenerationLimits: (token) => apiFetch('/api/admin/elearning/global-limits', { token }),
  updateGlobalGenerationLimits: (token, limits) => apiFetch('/api/admin/elearning/global-limits', { method: 'PUT', token, body: limits }),
  resetUserGenerationUsage: (token, userId, type) => apiFetch(`/api/admin/elearning/users/${userId}/reset-usage`, { method: 'POST', token, body: { type } }),
  getTopCategories: (token) => apiFetch('/api/admin/elearning/top-categories', { token }),
  
  // Atlas User Engagement Sub-Agent
  getAtlasDashboard: (token) => apiFetch('/api/admin/atlas/dashboard', { token }),
  getAtlasLogs: (token, limit = 20) => apiFetch(`/api/admin/atlas/logs?limit=${limit}`, { token }),
  getAtlasAtRiskUsers: (token) => apiFetch('/api/admin/atlas/at-risk', { token }),
  executeAtlasAction: (token, action, params = {}) => apiFetch('/api/admin/atlas/execute', { method: 'POST', token, body: { action, params } }),
  getAtlasUserEngagement: (token, userId) => apiFetch(`/api/admin/atlas/user/${userId}`, { token }),
  getAtlasSegment: (token, segment) => apiFetch(`/api/admin/atlas/segment/${segment}`, { token }),
  triggerWelcomeSequence: (token, userId) => apiFetch(`/api/admin/atlas/welcome/${userId}`, { method: 'POST', token }),
  sendAtlasNudge: (token, userId, nudgeType, customMessage) => apiFetch('/api/admin/atlas/nudge', { method: 'POST', token, body: { userId, nudgeType, customMessage } }),
  updateAtlasUserPreferences: (token, userId, preferences) => apiFetch(`/api/admin/atlas/user/${userId}/preferences`, { method: 'PATCH', token, body: preferences }),

  // QoreNova Command
  getCommandInbox: (token) => apiFetch('/api/admin/command/inbox', { token }),
  getCommandBrief: (token) => apiFetch('/api/admin/command/brief', { token }),
  getCrossLine: (token) => apiFetch('/api/admin/command/cross-line', { token }),
  executeCrossLine: (token, type, sourceId) => apiFetch('/api/admin/command/cross-line', { method: 'POST', token, body: { type, sourceId } }),
  executeCommandTool: (token, name, args = {}) => apiFetch('/api/admin/command/tool', { method: 'POST', token, body: { name, args } }),

  getConnectOverview: (token) => apiFetch('/api/admin/connect/overview', { token }),
  getConnectForum: (token, params = {}) => apiFetch('/api/admin/connect/forum' + toQuery(params), { token }),
  moderateForumPost: (token, id, action) => apiFetch(`/api/admin/connect/forum/${id}`, { method: 'PATCH', token, body: { action } }),

  getAcademyLabs: (token) => apiFetch('/api/admin/academy/labs', { token }),
  getAcademyCertificates: (token, params = {}) => apiFetch('/api/admin/academy/certificates' + toQuery(params), { token }),
  revokeCertificate: (token, id, reason) => apiFetch(`/api/admin/academy/certificates/${id}/revoke`, { method: 'POST', token, body: { reason } }),
  getAcademyTutor: (token) => apiFetch('/api/admin/academy/tutor', { token }),

  getSupportTickets: (token, params = {}) => apiFetch('/api/admin/support-tickets' + toQuery(params), { token }),
  getSupportTicketStats: (token) => apiFetch('/api/admin/support-tickets/stats', { token }),
  resolveSupportTicket: (token, id, summary) => apiFetch(`/api/admin/support-tickets/${id}/resolve`, { method: 'POST', token, body: { summary } }),
  addSupportTicketMessage: (token, id, content) => apiFetch(`/api/admin/support-tickets/${id}/messages`, { method: 'POST', token, body: { content } }),

  getEmailTemplates: (token, params = {}) => apiFetch('/api/admin/email-templates' + toQuery(params), { token }),
  updateEmailTemplate: (token, id, data) => apiFetch(`/api/admin/email-templates/${id}`, { method: 'PUT', token, body: data }),
  testEmailTemplate: (token, id, email) => apiFetch(`/api/admin/email-templates/${id}/test`, { method: 'POST', token, body: { email } }),

  getPaymentsDashboard: (token, params = {}) => apiFetch('/api/admin/payments-dashboard/dashboard' + toQuery(params), { token }),
  getPayments: (token, params = {}) => apiFetch('/api/admin/payments-dashboard' + toQuery(params), { token }),
  refundPayment: (token, id, data = {}) => apiFetch(`/api/admin/payments-dashboard/${id}/refund`, { method: 'POST', token, body: data }),
};

// Capstone API — the graded, first-class capstone object and its mentor review queue
export const CapstoneApi = {
  submit: (token, data) => apiFetch('/api/capstones', { method: 'POST', token, body: data }),
  mine: (token) => apiFetch('/api/capstones/mine', { token }),
  verifiedProfile: (token) => apiFetch('/api/capstones/verified-profile', { token }),
  reviewQueue: (token) => apiFetch('/api/capstones/review-queue', { token }),
  review: (token, id, data) => apiFetch(`/api/capstones/${id}/review`, { method: 'POST', token, body: data }),
};

// Contact API (public)
export const ContactApi = {
  submit: (data) => apiFetch('/api/contact', { method: 'POST', body: data }),
};

export default { apiFetch, AuthApi, EnrollmentApi, CourseApi, PaymentApi, VirtualLabApi, CoachingApi, AIApi, CareerApi, CapstoneApi, CommunityApi, AdminApi, ContactApi };
